import { html, nothing } from "lit";
import { classifyDiffLine, renderHighlightedDiffLine } from "./chat-file-diff-viewer.ts";
import { extractText } from "./message-extract.ts";

export const FILE_CHANGES_LABEL = "file-changes";
export const FILE_CHANGES_FLEET_LABEL = "file-changes-fleet";
const PAYLOAD_VERSION = 1;

export interface SessionArtifactFileSummary {
  path: string;
  op: "created" | "modified" | "deleted" | "manual";
  additions: number;
  deletions: number;
  sessionKey?: string;
  changeId?: string;
  previewLines?: string[];
}

export interface SessionArtifactSummaryTotals {
  files: number;
  created: number;
  modified: number;
  deleted: number;
  additions: number;
  deletions: number;
}

export interface SessionArtifactSummary {
  workspace: string;
  files: SessionArtifactFileSummary[];
  totals: SessionArtifactSummaryTotals;
  /** True when the underlying artifact registry was truncated at source. */
  truncated?: boolean;
  /** True when the response `files` slice hit the `limit` cap. Totals still reflect all files. */
  renderTruncated?: boolean;
  /** Effective limit applied (after clamping) when `files` was sliced. */
  appliedLimit?: number;
  bySession?: Record<string, SessionArtifactFileSummary[]>;
}

export type FileChangesPayload = {
  version: number;
  summary: SessionArtifactSummary;
  title?: string;
};

function stripInjectLabel(text: string, labels: string[]): string {
  let body = text.trim();
  for (const label of labels) {
    const prefix = `[${label}]`;
    if (body.startsWith(prefix)) {
      body = body.slice(prefix.length).trim();
      if (body.startsWith("\n")) {
        body = body.slice(1);
      }
    }
  }
  return body.trim();
}

export function isFileChangesMessage(message: unknown): boolean {
  const text = extractText(message) ?? "";
  // Detect by the inject label prefix. Do not require stopReason === "injected":
  // a regression left per-session `file-changes` injects tagged end_turn, and
  // those still must render as the file-changes panel (not raw JSON).
  return text.includes(`[${FILE_CHANGES_LABEL}]`) || text.includes(`[${FILE_CHANGES_FLEET_LABEL}]`);
}

export function parseFileChangesPayload(message: unknown): FileChangesPayload | null {
  const text = extractText(message) ?? "";
  const body = stripInjectLabel(text, [FILE_CHANGES_LABEL, FILE_CHANGES_FLEET_LABEL]);
  if (!body.startsWith("{")) {
    return null;
  }
  try {
    const parsed = JSON.parse(body) as FileChangesPayload;
    if (parsed?.version !== PAYLOAD_VERSION || !parsed.summary?.files) {
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
}

export function buildFileChangesInjectBody(
  summary: SessionArtifactSummary,
  opts?: { title?: string },
): string {
  const payload: FileChangesPayload = {
    version: PAYLOAD_VERSION,
    summary,
    title: opts?.title,
  };
  return JSON.stringify(payload);
}

export function formatFileChangesMarkdown(summary: SessionArtifactSummary, title?: string): string {
  const { totals, files } = summary;
  const head =
    title?.trim() ||
    `**${totals.files} file${totals.files === 1 ? "" : "s"} changed** (+${totals.additions} −${totals.deletions})`;
  const lines = files.map((f) => {
    const op = f.op === "manual" ? "modified" : f.op;
    return `- \`${f.path}\` — ${op} (+${f.additions} −${f.deletions})`;
  });
  return [head, "", ...lines].join("\n");
}

function opBadgeClass(op: SessionArtifactFileSummary["op"]): string {
  switch (op) {
    case "created":
      return "created";
    case "deleted":
      return "deleted";
    default:
      return "modified";
  }
}

function truncatePath(path: string, max = 56): string {
  if (path.length <= max) {
    return path;
  }
  const head = Math.floor((max - 1) / 2);
  const tail = max - 1 - head;
  return `${path.slice(0, head)}…${path.slice(-tail)}`;
}

function renderFilePreviewLines(lines: string[] | undefined) {
  if (!lines?.length) {
    return nothing;
  }
  return html`
    <div class="chat-file-changes__preview">
      ${lines.slice(0, 3).map((line) => renderHighlightedDiffLine(classifyDiffLine(line)))}
    </div>
  `;
}

export function renderChatFileChangesSummary(
  payload: FileChangesPayload,
  opts?: {
    defaultOpen?: boolean;
    onFileClick?: (changeId: string, path: string, artifactSessionKey?: string) => void;
  },
) {
  const { summary, title } = payload;
  const { totals, files } = summary;
  if (files.length === 0) {
    return nothing;
  }
  const header = title?.trim() || `${totals.files} file${totals.files === 1 ? "" : "s"} changed`;
  const open = opts?.defaultOpen ?? files.length <= 8;

  return html`
    <details class="chat-file-changes" ?open=${open}>
      <summary class="chat-file-changes__summary">
        <span class="chat-file-changes__title">${header}</span>
        <span class="chat-file-changes__totals">
          <span class="chat-file-changes__add">+${totals.additions}</span>
          <span class="chat-file-changes__del">−${totals.deletions}</span>
        </span>
      </summary>
      <ul class="chat-file-changes__list" role="list">
        ${files.map((f) => {
          const changeId = f.changeId?.trim() ?? "";
          const canClick = Boolean(changeId && opts?.onFileClick);
          return html`
            <li class="chat-file-changes__row-wrap" role="listitem">
              <button
                type="button"
                class="chat-file-changes__row ${canClick ? "chat-file-changes__row--clickable" : ""}"
                ?disabled=${!canClick}
                @click=${() => {
                  if (changeId) {
                    opts?.onFileClick?.(changeId, f.path, f.sessionKey);
                  }
                }}
              >
                <span class="chat-file-changes__op chat-file-changes__op--${opBadgeClass(f.op)}">
                  ${f.op === "manual" ? "modified" : f.op}
                </span>
                <span class="chat-file-changes__path mono" title=${f.path}>
                  ${truncatePath(f.path)}
                </span>
                <span class="chat-file-changes__stats">
                  <span class="chat-file-changes__add">+${f.additions}</span>
                  <span class="chat-file-changes__del">−${f.deletions}</span>
                </span>
              </button>
              ${renderFilePreviewLines(f.previewLines)}
            </li>
          `;
        })}
      </ul>
      ${
        summary.truncated
          ? html` <p class="chat-file-changes__truncated muted">Older changes omitted.</p> `
          : nothing
      }
    </details>
  `;
}
